import { Box, Grid, List, Stack, styled } from '@mui/material';
import { FieldValues, FormProvider, SubmitHandler, useFormContext } from 'react-hook-form';
import { WsButton, WsDrawer } from 'wisestep-base';

import CheckboxListFilter from '../../../../components/filters/components/CheckboxListFilter';
import RadioListFilter from '../../../../components/filters/components/RadioListFilter';
import BasicSearchForm from '../../types/IBasicSearchForm';
import CareerBuilderAdvanceSearch from '../types/ICareerBuilderEdgeAdvanceSearch';

interface AdvanceFilterProps {
  open: boolean;
  onClose: () => void;
  performSearch: (values: BasicSearchForm & CareerBuilderAdvanceSearch) => void;
  facets: any;
}

const WorkAuthorization = new Map<string, string>();
WorkAuthorization.set('CTAY', 'Can work for any employer');
WorkAuthorization.set('CTCT', 'US Citizen');
WorkAuthorization.set('CTEM', 'H1 Visa');
WorkAuthorization.set('CTGR', 'Green Card Holder');
WorkAuthorization.set('CTNO', 'Need H1 Visa Sponsor');
WorkAuthorization.set('CTNS', 'Not Speciﬁed');
WorkAuthorization.set('EATN', 'TN Permit Holder');
WorkAuthorization.set('EAEA', 'Employment Authorization Document');

const DrawerFooter = styled(Grid)(() => ({
  display: 'flex',
  alignItems: 'center',
  position: 'fixed',
  bottom: 1,
  left: 2,
  right: 2,
  justifyContent: 'flex-end',
}));

const AdvanceFilter = ({ open, onClose, performSearch, facets }: AdvanceFilterProps) => {
  const methods = useFormContext();
  const { handleSubmit, reset } = methods;

  const getNames = (facetsArray) => {
    const resultList = {};
    facetsArray.map((element) => {
      resultList[element.Name] = element.Name;
    });
    return resultList;
  };

  const getWorkStatusNames = (facetsArray) => {
    const resultList = {};
    facetsArray.map((element) => {
      resultList[element.Name] = WorkAuthorization.get(element.Name);
    });
    return resultList;
  };

  return (
    <FormProvider {...methods}>
      <WsDrawer
        title="Advanced Filters"
        open={open}
        onClose={() => onClose()}
        size="medium"
        headerSize="medium"
      >
        <Box
          component="form"
          role="presentation"
          onSubmit={handleSubmit(performSearch as SubmitHandler<FieldValues>)}
          onReset={() => reset()}
        >
          <Box pb={7}>
            <List>
              <RadioListFilter
                name="searchPattern"
                row={false}
                options={[
                  { label: 'Match All Keywords', value: 'All' },
                  { label: 'Match Any Keywords', value: 'Any' },
                  { label: 'Match Exact Keywords', value: 'Exact' },
                  { label: 'Match keyword using Boolean Logic', value: 'Boo' },
                ]}
                title="Search Pattern"
              />

              {facets ? (
                <>
                  <CheckboxListFilter
                    name="AcademicDiscipline"
                    options={getNames(facets.AcademicDiscipline)}
                    title="AcademicDiscipline"
                  />
                  <CheckboxListFilter name="City" options={getNames(facets.City)} title="City" />
                  <CheckboxListFilter
                    name="Country"
                    options={getNames(facets.Country)}
                    title="Country"
                  />
                  <CheckboxListFilter
                    name="JobTitle"
                    options={getNames(facets.JobTitle)}
                    title="JobTitle"
                  />
                  <CheckboxListFilter
                    name="CurrentEmployer"
                    options={getNames(facets.CurrentEmployer)}
                    title="CurrentEmployer"
                  />
                  <CheckboxListFilter
                    name="ContactInfo"
                    options={getNames(facets.ContactInfo)}
                    title="ContactInfo"
                  />
                  <CheckboxListFilter
                    name="CurrentlyEmployed"
                    options={getNames(facets.CurrentlyEmployed)}
                    title="CurrentlyEmployed"
                  />
                  <CheckboxListFilter
                    name="DegreeLevel"
                    options={getNames(facets.DegreeLevel)}
                    title="DegreeLevel"
                  />
                  <CheckboxListFilter
                    name="Industry"
                    options={getNames(facets.Industry)}
                    title="Industry"
                  />
                  <CheckboxListFilter
                    name="PreviousEmployer"
                    options={getNames(facets.PreviousEmployer)}
                    title="PreviousEmployer"
                  />
                  <CheckboxListFilter name="Skill" options={getNames(facets.Skill)} title="Skill" />
                  <CheckboxListFilter
                    name="Source"
                    options={getNames(facets.Source)}
                    title="Source"
                  />
                  <CheckboxListFilter
                    name="HasResume"
                    options={getNames(facets.HasResume)}
                    title="HasResume"
                  />
                  <CheckboxListFilter name="State" options={getNames(facets.State)} title="State" />
                  <CheckboxListFilter
                    name="DesiredJobType"
                    options={getNames(facets.DesiredJobType)}
                    title="DesiredJobType"
                  />
                  <CheckboxListFilter
                    name="WorkStatus"
                    options={getWorkStatusNames(facets.WorkStatus)}
                    title="WorkStatus"
                  />
                  <CheckboxListFilter
                    name="YearsOfExperience"
                    options={getNames(facets.YearsOfExperience)}
                    title="YearsOfExperience"
                  />
                  <CheckboxListFilter
                    name="EdgeScore"
                    options={getNames(facets.EdgeScore)}
                    title="EdgeScore"
                  />
                  <CheckboxListFilter
                    name="ResumeModified"
                    options={getNames(facets.ResumeModified)}
                    title="ResumeModified"
                  />
                  <CheckboxListFilter
                    name="WillingToRelocate"
                    options={getNames(facets.WillingToRelocate)}
                    title="willing To Relocate"
                  />
                  <CheckboxListFilter
                    name="SecurityClearance"
                    options={getNames(facets.SecurityClearance)}
                    title="Security Clearance"
                  />
                </>
              ) : (
                <></>
              )}
            </List>
          </Box>
          <DrawerFooter>
            <Stack p={1} spacing={2} direction="row">
              <WsButton variant="outlined" size="large" type="reset">
                Reset
              </WsButton>
              <WsButton variant="contained" size="large" type="submit">
                Apply
              </WsButton>
            </Stack>
          </DrawerFooter>
        </Box>
      </WsDrawer>
    </FormProvider>
  );
};

export default AdvanceFilter;
